import { useStyletron } from 'baseui';
import { LabelXSmall } from 'baseui/typography';
import { useTranslation } from 'react-i18next';

import { getPasswordRuleLabels, getPasswordRules, getPasswordTips } from 'shared/helpers';

function PasswordTipsList({ compact = false, value = '' }: { compact?: boolean; value?: string }) {
	const { t } = useTranslation(['common', 'components', 'errors', 'shopify']);
	const [css, theme] = useStyletron();
	const items = getPasswordTips(t);
	const passwordRules = getPasswordRules(value);
	const passwordRuleLabels = getPasswordRuleLabels(t);
	const ruleItems = [
		{
			isValid: passwordRules.isValidLength,
			label: passwordRuleLabels.lengthRange,
		},
		{
			isValid: passwordRules.hasNonSpaceCharacter,
			label: passwordRuleLabels.hasNonSpaceCharacter,
		},
	];

	const renderRuleIcon = (isValid: boolean) => (
		<span
			className={css({
				alignItems: 'center',
				color: isValid ? theme.colors.primaryA : theme.colors.inputBorderError,
				display: 'inline-flex',
				flexShrink: 0,
				height: compact ? '0.875rem' : '1rem',
				justifyContent: 'center',
				width: compact ? '0.875rem' : '1rem',
			})}
		>
			{isValid ? (
				<span
					className={css({
						borderBottomColor: 'currentColor',
						borderBottomStyle: 'solid',
						borderBottomWidth: '0.125rem',
						borderRightColor: 'currentColor',
						borderRightStyle: 'solid',
						borderRightWidth: '0.125rem',
						height: compact ? '0.5rem' : '0.625rem',
						transform: 'rotate(45deg)',
						width: compact ? '0.3125rem' : '0.375rem',
					})}
				/>
			) : (
				<span
					className={css({
						backgroundColor: 'currentColor',
						borderRadius: '50%',
						height: compact ? '0.375rem' : '0.4375rem',
						width: compact ? '0.375rem' : '0.4375rem',
					})}
				/>
			)}
		</span>
	);

	return (
		<div
			className={css({
				marginTop: compact ? 0 : '0.25rem',
				width: '100%',
				paddingBottom: compact ? '0.125rem' : '0.25rem',
			})}
		>
			<ul
				className={css({
					margin: 0,
					padding: 0,
				})}
			>
				{ruleItems.map((rule) => (
					<li
						key={rule.label}
						className={css({
							alignItems: 'center',
							color: rule.isValid ? theme.colors.primaryA : theme.colors.inputBorderError,
							display: 'flex',
							gap: '0.5rem',
							listStyleType: 'none',
							marginBottom: compact ? '0.375rem' : '0.5rem',
						})}
					>
						{renderRuleIcon(rule.isValid)}
						<LabelXSmall
							className={css({
								color: rule.isValid ? theme.colors.primaryA : theme.colors.inputBorderError,
								fontSize: compact ? '0.75rem !important' : '0.8125rem !important',
								fontWeight: 600,
								lineHeight: compact ? '1.0625rem !important' : '1.1875rem !important',
								whiteSpace: 'normal',
							})}
						>
							{rule.label}
						</LabelXSmall>
					</li>
				))}
			</ul>
			<div
				className={css({
					borderTopColor: theme.colors.accent100,
					borderTopStyle: 'solid',
					borderTopWidth: '1px',
					marginBottom: compact ? '0.5rem' : '0.625rem',
					marginTop: compact ? '0.125rem' : '0.25rem',
				})}
			/>
			<ul
				className={css({
					color: theme.colors.primaryA,
					margin: 0,
					paddingLeft: compact ? '1rem' : '1.125rem',
				})}
			>
				{items.map((item) => (
					<li key={item} className={css({ marginBottom: compact ? '0.375rem' : '0.5rem' })}>
						<LabelXSmall
							className={css({
								color: theme.colors.primaryA,
								fontSize: compact ? '0.75rem !important' : '0.8125rem !important',
								lineHeight: compact ? '1.0625rem !important' : '1.1875rem !important',
								whiteSpace: 'normal',
								wordBreak: 'break-word',
							})}
						>
							{item}
						</LabelXSmall>
					</li>
				))}
			</ul>
		</div>
	);
}

PasswordTipsList.defaultProps = {
	compact: false,
	value: '',
};

export default PasswordTipsList;
